import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Table, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';
import { printGuestFolio, printRoomInvoice } from '../utils/print';

const CHARGE_TYPES = [
  { value:'food',       label:'Food & Restaurant',     icon:'🍽️' },
  { value:'bar',        label:'Bar & Beverages',        icon:'🍹' },
  { value:'pool',       label:'Swimming Pool',          icon:'🏊' },
  { value:'laundry',    label:'Laundry',                icon:'👔' },
  { value:'spa',        label:'Spa & Wellness',         icon:'💆' },
  { value:'minibar',    label:'Mini Bar',               icon:'🧴' },
  { value:'telephone',  label:'Telephone',              icon:'📞' },
  { value:'conference', label:'Conference',             icon:'🏛️' },
  { value:'other',      label:'Other / Miscellaneous',  icon:'📌' },
];
const TYPE_ICON = Object.fromEntries(CHARGE_TYPES.map(t => [t.value, t.icon]));
const TYPE_LABEL = Object.fromEntries(CHARGE_TYPES.map(t => [t.value, t.label]));

export default function GuestFolioPage({ notify }) {
  const [guests, setGuests]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [folio, setFolio]       = useState(null);
  const [tab, setTab]           = useState('charges');
  const [search, setSearch]     = useState('');

  // Manual charge form
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [chargeForm, setChargeForm]         = useState({ charge_type:'other', description:'', amount:'', quantity:1 });
  const [chargeLoading, setChargeLoading]   = useState(false);

  // Settlement
  const [settlementModal, setSettlementModal] = useState(false);
  const [payMode, setPayMode]                 = useState('cash');
  const [txCode, setTxCode]                   = useState('');
  const [settling, setSettling]               = useState(false);

  const loadGuests = useCallback(async () => {
    const r = await api.getGuests();
    const all = r.data.results || r.data;
    setGuests(all.filter(g => g.status === 'checked-in'));
  }, []);

  const loadFolio = useCallback(async (guestId) => {
    if (!guestId) return;
    try {
      const r = await api.getGuestFolio(guestId);
      setFolio(r.data);
    } catch(e) { notify('Failed to load folio', 'error'); }
  }, [notify]);

  useEffect(() => { loadGuests(); }, [loadGuests]);

  const selectGuest = async (g) => {
    setSelected(g);
    setFolio(null);
    setTab('charges');
    await loadFolio(g.id);
  };

  const postCharge = async () => {
    if (!chargeForm.description || !chargeForm.amount) { notify('Description and amount required', 'error'); return; }
    setChargeLoading(true);
    try {
      await api.postFolioCharge({ ...chargeForm, guest: selected.id, amount: parseFloat(chargeForm.amount), quantity: parseInt(chargeForm.quantity) });
      await loadFolio(selected.id);
      await loadGuests();
      setChargeForm({ charge_type:'other', description:'', amount:'', quantity:1 });
      setShowChargeForm(false);
      notify('Charge posted to folio!');
    } catch(e) { notify(e.response?.data?.error || 'Error posting charge', 'error'); }
    finally { setChargeLoading(false); }
  };

  const settleAll = async () => {
    setSettling(true);
    try {
      const unsettled = folio?.unsettled || [];
      if (unsettled.length === 0) { notify('No outstanding charges to settle', 'error'); setSettlementModal(false); return; }

      await api.settleFolioCharges({ guest: selected.id });
      await loadFolio(selected.id);
      await loadGuests();
      setSettlementModal(false);
      setTxCode('');
      notify(`Folio settled — ${fmtKES(folio.folio_balance)} charged via ${payMode.toUpperCase()}`);
    } catch(e) { notify(e.response?.data?.error || 'Settlement failed', 'error'); }
    finally { setSettling(false); }
  };

  const doCheckout = async (force = false) => {
    try {
      await api.checkoutGuest(selected.id);
      if (folio) printGuestFolio(folio);
      await loadGuests();
      setSelected(null);
      setFolio(null);
      notify(`${selected.name} checked out. Folio printed.`);
    } catch(e) {
      const err = e.response?.data;
      if (err?.requires_settlement) {
        notify(`Cannot check out — ${err.unsettled_count} unsettled charge(s) totalling ${fmtKES(err.unsettled_total)}. Please settle first.`, 'error');
      } else {
        notify(e.response?.data?.error || 'Checkout error', 'error');
      }
    }
  };

  const filtered = search ? guests.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    g.room_number?.toString().includes(search) ||
    g.phone.includes(search)
  ) : guests;

  const unsettledCharges = folio?.unsettled || [];
  const allCharges = folio?.charges || [];

  return (
    <div>
      <PageHeader icon="📒" title="Guest Folios" sub="Room service charges — post, view & settle guest accounts" />

      <StatCards stats={[
        { label: 'Checked-In Guests', value: guests.length, color: '#3498db' },
        { label: 'Open Folios',       value: guests.filter(g => g.folio_balance > 0).length, color: '#e67e22' },
        { label: 'Total Outstanding', value: fmtKES(guests.reduce((s, g) => s + parseFloat(g.folio_balance || 0), 0)), color: '#c8a96e' },
        { label: 'Selected Guest',    value: selected ? `Rm ${selected.room_number}` : '—', color: '#2ecc71' },
      ]} />

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' }}>

        {/* ── Guest List ── */}
        <div>
          <Card title={`In-House (${guests.length})`}>
            <input
              style={{ ...inputStyle, marginBottom: 10, fontSize: 13 }}
              placeholder="🔍 Search guest…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {filtered.length === 0 && <Empty text="No guests checked in" />}
            {filtered.map(g => {
              const isSelected = selected?.id === g.id;
              const hasBalance = parseFloat(g.folio_balance) > 0;
              return (
                <div
                  key={g.id}
                  onClick={() => selectGuest(g)}
                  style={{
                    padding: '12px 14px', marginBottom: 6, borderRadius: 4, cursor: 'pointer',
                    background: isSelected ? '#1a2a1a' : '#111',
                    border: `1px solid ${isSelected ? '#2ecc71' : hasBalance ? '#c8a96e44' : 'var(--borderMid)'}`,
                    transition: 'all .15s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: isSelected ? '#2ecc71' : '#eee', fontSize: 13 }}>{g.name}</div>
                      <div style={{ fontSize: 11, color:'var(--textFaint)', marginTop: 2 }}>Room {g.room_number} · {g.room_type}</div>
                      <div style={{ fontSize: 11, color:'var(--textFaint)', marginTop: 1 }}>{g.check_in} → {g.check_out}</div>
                    </div>
                    {hasBalance && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, color: '#c8a96e', fontWeight: 700 }}>{fmtKES(g.folio_balance)}</div>
                        <div style={{ fontSize: 9, color:'var(--textMuted)', textTransform: 'uppercase', letterSpacing: 1 }}>Outstanding</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        </div>

        {/* ── Folio Detail ── */}
        <div>
          {!selected && (
            <Card title="Guest Folio">
              <div style={{ textAlign: 'center', padding: '60px 20px', color:'var(--textFaint)' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📒</div>
                <div style={{ fontSize: 15, marginBottom: 8 }}>Select a guest to view their folio</div>
                <div style={{ fontSize: 13, color: '#444' }}>All room service charges, food, bar, pool and other incidentals appear here</div>
              </div>
            </Card>
          )}

          {selected && !folio && (
            <Card title="Loading folio…">
              <div style={{ textAlign: 'center', padding: '40px', color:'var(--textFaint)' }}>Loading charges…</div>
            </Card>
          )}

          {selected && folio && (
            <>
              {/* Guest summary bar */}
              <div style={{ background: '#0f1a0f', border: '1px solid #2ecc7133', borderRadius: 4, padding: '16px 20px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color:'var(--textPrimary)' }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color:'var(--textMuted)', marginTop: 2 }}>
                    Room {selected.room_number} ({selected.room_type}) · {selected.check_in} → {selected.check_out} · {selected.nights} night{selected.nights !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color:'var(--textFaint)', textTransform: 'uppercase', letterSpacing: 1 }}>Room Charges</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color:'var(--textMuted)' }}>{fmtKES(folio.room_charge)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color:'var(--textFaint)', textTransform: 'uppercase', letterSpacing: 1 }}>Incidentals</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: unsettledCharges.length ? '#e67e22' : '#2ecc71' }}>{fmtKES(folio.folio_balance)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#c8a96e', textTransform: 'uppercase', letterSpacing: 1 }}>Total Bill</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#c8a96e' }}>{fmtKES(folio.total_bill)}</div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <button style={btnPrimary} onClick={() => setShowChargeForm(true)}>+ Post Charge</button>
                {unsettledCharges.length > 0 && (
                  <button style={{ ...btnPrimary, background: '#e67e22' }} onClick={() => setSettlementModal(true)}>
                    💰 Settle Incidentals ({fmtKES(folio.folio_balance)})
                  </button>
                )}
                <button style={{ ...btnSm, background: '#1a2a3a' }} onClick={() => printGuestFolio(folio)}>🖨 Print Folio</button>
                <button style={{ ...btnSm, background: '#3a1010' }} onClick={() => doCheckout(false)}>
                  🚪 Check Out
                </button>
              </div>

              <TabBar tabs={[
                { id: 'charges', label: `All Charges (${allCharges.length})`,        icon: '📋' },
                { id: 'pending', label: `Outstanding (${unsettledCharges.length})`,  icon: '⏳' },
                { id: 'summary', label: 'Summary by Type',                           icon: '📊' },
              ]} active={tab} onChange={setTab} />

              {/* All charges table */}
              {tab === 'charges' && (
                <Card title={`Folio — ${selected.name}`}>
                  {allCharges.length === 0 ? (
                    <Empty text="No charges posted yet" />
                  ) : (
                    <Table
                      headers={['Date', 'Type', 'Description', 'Qty', 'Amount', 'Status', 'Posted By']}
                      rows={allCharges.map(c => [
                        <span style={{ fontSize: 12, color:'var(--textMuted)' }}>{new Date(c.created_at).toLocaleDateString('en-KE')}</span>,
                        <span style={{ color: '#c8a96e', fontSize: 12 }}>{TYPE_ICON[c.charge_type]} {TYPE_LABEL[c.charge_type]}</span>,
                        <span style={{ color:'var(--textSecond)', fontSize: 13 }}>{c.description}</span>,
                        c.quantity > 1 ? c.quantity : '—',
                        <span style={{ fontWeight: 700, color: '#c8a96e' }}>{fmtKES(c.amount)}</span>,
                        c.settled
                          ? <span style={{ color: '#2ecc71', fontSize: 11, fontWeight: 600 }}>✓ Settled</span>
                          : <span style={{ color: '#e67e22', fontSize: 11, fontWeight: 600 }}>● Pending</span>,
                        <span style={{ fontSize: 11, color:'var(--textFaint)' }}>{c.posted_by_name || 'System'}</span>,
                      ])}
                    />
                  )}
                </Card>
              )}

              {/* Outstanding only */}
              {tab === 'pending' && (
                <Card title={`Outstanding Charges — ${fmtKES(folio.folio_balance)}`}>
                  {unsettledCharges.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#2ecc71' }}>
                      <div style={{ fontSize: 32, marginBottom: 10 }}>✓</div>
                      <div>All charges settled — folio is clear</div>
                    </div>
                  ) : (
                    <Table
                      headers={['Date', 'Type', 'Description', 'Amount']}
                      rows={unsettledCharges.map(c => [
                        <span style={{ fontSize: 12, color:'var(--textMuted)' }}>{new Date(c.created_at).toLocaleDateString('en-KE')}</span>,
                        <span style={{ color: '#c8a96e', fontSize: 12 }}>{TYPE_ICON[c.charge_type]} {TYPE_LABEL[c.charge_type]}</span>,
                        c.description,
                        <span style={{ fontWeight: 700, color: '#e67e22' }}>{fmtKES(c.amount)}</span>,
                      ])}
                    />
                  )}
                </Card>
              )}

              {/* Summary by type */}
              {tab === 'summary' && (
                <Card title="Charges Summary by Category">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
                    <div style={{ background: '#0f1a0f', border: '1px solid #2a3a2a', borderRadius: 4, padding: '16px 18px' }}>
                      <div style={{ fontSize: 11, color:'var(--textFaint)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>🛏️ Room Accommodation</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color:'var(--textPrimary)' }}>{fmtKES(folio.room_charge)}</div>
                      <div style={{ fontSize: 11, color:'var(--textFaint)', marginTop: 2 }}>{selected.nights} night{selected.nights !== 1 ? 's' : ''} · Paid at check-in</div>
                    </div>
                    {CHARGE_TYPES.map(type => {
                      const typeCharges = allCharges.filter(c => c.charge_type === type.value);
                      if (typeCharges.length === 0) return null;
                      const total = typeCharges.reduce((s, c) => s + parseFloat(c.amount), 0);
                      const unsettledCount = typeCharges.filter(c => !c.settled).length;
                      return (
                        <div key={type.value} style={{ background: 'var(--bgInput)', border: '1px solid #2a2a2a', borderRadius: 4, padding: '16px 18px' }}>
                          <div style={{ fontSize: 11, color:'var(--textFaint)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{type.icon} {type.label}</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: unsettledCount ? '#e67e22' : '#2ecc71' }}>{fmtKES(total)}</div>
                          <div style={{ fontSize: 11, color:'var(--textFaint)', marginTop: 2 }}>{typeCharges.length} charge{typeCharges.length !== 1 ? 's' : ''}{unsettledCount ? ` · ${unsettledCount} pending` : ' · all settled'}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 16, padding: '14px 18px', background: '#1a1204', border: '1px solid #c8a96e33', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, color:'var(--textMuted)' }}>Room: {fmtKES(folio.room_charge)} + Incidentals: {fmtKES(folio.folio_balance)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#c8a96e', textTransform: 'uppercase', letterSpacing: 1 }}>Grand Total</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#c8a96e' }}>{fmtKES(folio.total_bill)}</div>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Post Charge Modal ── */}
      {showChargeForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--bgModal)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#121212', border: '1px solid #2a2a2a', borderRadius: 6, padding: 28, maxWidth: 460, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: '#c8a96e', letterSpacing: 3, textTransform: 'uppercase' }}>Post Charge</div>
                <div style={{ fontSize: 17, fontWeight: 700, color:'var(--textPrimary)' }}>{selected?.name} · Room {selected?.room_number}</div>
              </div>
              <button onClick={() => setShowChargeForm(false)} style={{ background: 'none', border: 'none', color:'var(--textFaint)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <Field label="Charge Type">
                  <select style={inputStyle} value={chargeForm.charge_type} onChange={e => setChargeForm({ ...chargeForm, charge_type: e.target.value })}>
                    {CHARGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <Field label="Description *">
                  <input style={inputStyle} value={chargeForm.description} onChange={e => setChargeForm({ ...chargeForm, description: e.target.value })} placeholder="e.g. Laundry — 3 shirts, 2 trousers" />
                </Field>
              </div>
              <div>
                <Field label="Amount (KES) *">
                  <input style={inputStyle} type="number" min="1" value={chargeForm.amount} onChange={e => setChargeForm({ ...chargeForm, amount: e.target.value })} placeholder="0.00" />
                </Field>
              </div>
              <div>
                <Field label="Quantity">
                  <input style={inputStyle} type="number" min="1" value={chargeForm.quantity} onChange={e => setChargeForm({ ...chargeForm, quantity: e.target.value })} />
                </Field>
              </div>
            </div>

            {chargeForm.amount && (
              <div style={{ background: 'var(--bgInput)', borderRadius: 3, padding: '10px 14px', margin: '12px 0', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color:'var(--textMuted)' }}>Total to post</span>
                <span style={{ fontWeight: 700, color: '#c8a96e', fontSize: 16 }}>{fmtKES(parseFloat(chargeForm.amount || 0) * parseInt(chargeForm.quantity || 1))}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button style={{ ...btnPrimary, flex: 1 }} onClick={postCharge} disabled={chargeLoading}>
                {chargeLoading ? 'Posting…' : '✓ Post to Folio'}
              </button>
              <button style={{ ...btnSm, background: 'var(--borderMid)', padding: '0 20px' }} onClick={() => setShowChargeForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Settlement Modal ── */}
      {settlementModal && folio && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--bgModal)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#121212', border: '1px solid #2a2a2a', borderRadius: 6, padding: 28, maxWidth: 480, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: '#c8a96e', letterSpacing: 3, textTransform: 'uppercase' }}>Settle Incidentals</div>
                <div style={{ fontSize: 17, fontWeight: 700, color:'var(--textPrimary)' }}>{selected?.name}</div>
              </div>
              <button onClick={() => setSettlementModal(false)} style={{ background: 'none', border: 'none', color:'var(--textFaint)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Charge breakdown */}
            <div style={{ background: '#0e0e0e', borderRadius: 4, padding: '10px 12px', marginBottom: 16, maxHeight: 200, overflowY: 'auto' }}>
              {unsettledCharges.map(c => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #1a1a1a', fontSize: 13 }}>
                  <span style={{ color:'var(--textSecond)' }}>{TYPE_ICON[c.charge_type]} {c.description}</span>
                  <span style={{ color: '#e67e22', fontWeight: 600 }}>{fmtKES(c.amount)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', fontWeight: 700 }}>
                <span style={{ color:'var(--textPrimary)' }}>TOTAL OUTSTANDING</span>
                <span style={{ color: '#c8a96e', fontSize: 18 }}>{fmtKES(folio.folio_balance)}</span>
              </div>
            </div>

            <Field label="Payment Method">
              <select style={inputStyle} value={payMode} onChange={e => setPayMode(e.target.value)}>
                <option value="cash">💵 Cash</option>
                <option value="mpesa">📱 M-Pesa</option>
                <option value="bank">🏦 Bank Transfer</option>
                <option value="pdq">💳 PDQ / Card</option>
                <option value="room-charge">🏨 Add to Room Bill</option>
              </select>
            </Field>

            {['mpesa','bank','pdq'].includes(payMode) && (
              <Field label="Transaction / Reference Code *">
                <input style={{ ...inputStyle, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700 }}
                  value={txCode} onChange={e => setTxCode(e.target.value.toUpperCase())}
                  placeholder={payMode === 'mpesa' ? 'e.g. QHX7Y3K2AB' : 'Transaction reference'}
                />
              </Field>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button style={{ ...btnPrimary, flex: 1, background: '#2ecc71', color: '#111' }} onClick={settleAll} disabled={settling}>
                {settling ? 'Processing…' : `✓ Settle ${fmtKES(folio.folio_balance)}`}
              </button>
              <button style={{ ...btnSm, background: 'var(--borderMid)', padding: '0 20px' }} onClick={() => setSettlementModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
